// services/components/strategies/search-bar/SearchBar.tsx
import * as React from 'react'
import {
  Combobox,
  ComboboxButton,
  ComboboxInput,
  ComboboxOption,
  ComboboxOptions,
} from '@headlessui/react'
import { cn } from '@/lib/ui/utils'
import Icon from '../common/Icon'

export interface PlaceSuggestion {
  id: string
  title: string
  url: string
}

export interface SearchBarProps {
  id: string
  selected: PlaceSuggestion | null
  suggestions: PlaceSuggestion[]
  placeholder: string
  autoFocus: boolean
  setQuery: (value: string) => void
  setSelected: (value: PlaceSuggestion | null) => void
  onClose: () => void
  onOpen: () => void
  onEnter: () => void
  onSearchGPS: () => void
  loading?: boolean
  noResults?: boolean
}

const SearchBar: React.FC<SearchBarProps> = ({
  id,
  selected,
  suggestions,
  autoFocus,
  setQuery,
  setSelected,
  placeholder,
  onEnter,
  onClose,
  onOpen,
  onSearchGPS,
  loading = false,
  noResults = false,
}) => {
  const unicode = (text: string) => {
    const utf8Bytes = new TextEncoder().encode(text)
    const binaryString = Array.from(utf8Bytes, (byte) =>
      String.fromCharCode(byte),
    ).join('')
    return btoa(binaryString)
  }

  return (
    <Combobox
      immediate
      value={selected}
      onChange={setSelected}
      onClose={onClose}
      data-cy={`search-bar-${id}`}
    >
      {({ open }) => (
        <div className={cn('w-full relative bg-white z-[1100]')}>
          <div className="relative w-full">
            <ComboboxButton
              className="group absolute inset-y-0 left-2 px-2.5 py-4"
              aria-label="Suche"
              data-cy={`search-bar-${id}-button`}
            >
              <Icon name="search-1" size={16} />
            </ComboboxButton>

            <ComboboxInput
              data-cy={`search-bar-${id}-input`}
              onClick={onOpen}
              autoFocus={autoFocus}
              placeholder={placeholder}
              aria-label={placeholder}
              autoComplete="off"
              className={cn(
                'w-full placeholder:text-grey-400 placeholder:text-sm placeholder:not-italic text-sm text-black bg-transparent px-10 h-[48px] bg-white',
                'focus:outline-none focus:shadow-none',
                open
                  ? 'border-t border-l border-r border-cloudblue-300 rounded-t-3xl'
                  : 'border rounded-full border-grey-300 hover:border-grey-400 transition-colors duration-200',
              )}
              displayValue={(s: PlaceSuggestion) => s?.title}
              onChange={(e) => setQuery(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') onEnter()
                if (e.key === 'Escape') onClose()
              }}
            />

            {/* Right-side adornment: spinner when loading */}
            <div className="absolute inset-y-0 right-3 flex items-center">
              {loading ? (
                <Icon name="loading-spin" size={16} className="animate-spin" />
              ) : null}
            </div>
          </div>

          <ComboboxOptions
            unmount={false}
            portal={false}
            transition
            className={cn(
              'absolute text-sm top-0 w-[var(--input-width)] space-y-2 py-2 mt-10 bg-white border-r border-l border-b border-cloudblue-300 rounded-b-3xl z-[1000] empty:invisible cursor-pointer',
              open
                ? 'transition duration-100 ease-in shadow-xl'
                : 'data-[leave]:data-[closed]:opacity-0',
            )}
          >
            <ComboboxOption
              value={''}
              onClick={onSearchGPS}
              className="group items-center gap-2 rounded-lg py-1.5 px-6 select-none data-[focus]:bg-white/10 text-black hover:text-grey-400 transition-all duration-100 ease-in-out inline-flex gap-x-2"
              data-cy={`search-bar-${id}-gps`}
            >
              <Icon className="size-4 group-hover:fill-grey-400" name="poi" />
              Dein Standort
            </ComboboxOption>

            {/* Loading & Empty messages inside the list */}
            {loading && (
              <div className="py-1.5 px-6 text-grey-500 flex items-center gap-2">
                <Icon name="loading-spin" size={14} className="animate-spin" />
                Suchen …
              </div>
            )}

            {!loading && noResults && (
              <div className="py-1.5 px-6 text-grey-500">Keine Treffer</div>
            )}

            {!loading &&
              suggestions?.map((s, i) => (
                <ComboboxOption
                  key={unicode(s.id + s.title + s.url)}
                  data-cy={`search-bar-${id}-suggestion-${s.id}-${i}`}
                  value={s}
                  className="group flex items-center gap-2 rounded-lg py-1.5 px-6 select-none data-[focus]:bg-white/10 text-black hover:text-grey-400 transition-all duration-100 ease-in-out"
                >
                  <div>{s.title}</div>
                </ComboboxOption>
              ))}
          </ComboboxOptions>
        </div>
      )}
    </Combobox>
  )
}

export default SearchBar
